import type {
  AssetRecord,
  DesignDocument,
  DesignImageObject,
  DesignObject,
  DesignTextObject,
  MmRect,
  ProductConfig,
  ProductViewId,
} from '../domain/types';
import { isVisibleArtworkObject } from '../domain/design';
import { fonts } from '../domain/fonts';
import { hasActivePrintEffects } from '../domain/print-effects';
import { formatA4SheetBlockingMessage, inspectA4SheetFit } from '../export/a4-sheet';
import { partitionObjectsIntoA4Transfers } from '../export/transfer-groups';
import {
  isColourAvailableForVariant,
  isSizeSelectable,
  resolvePrintableArea,
  resolveProductVariant,
  resolveSizeProfile,
} from '../products';
import { activeImageBorders } from '../rendering/image-borders';
import { effectiveTextHeightMm, objectBounds, rectContainsObject, unionRects } from './geometry';

export type ValidationSeverity = 'error' | 'warning';

export type ValidationCode =
  | 'empty-design'
  | 'invalid-product-variant'
  | 'invalid-product-size'
  | 'invalid-product-colour'
  | 'missing-size-profile'
  | 'unsupported-view'
  | 'unsupported-technology'
  | 'surface-size-mismatch'
  | 'object-out-of-bounds'
  | 'object-outside-safe-zone'
  | 'missing-asset'
  | 'distorted-image-aspect'
  | 'low-image-dpi'
  | 'image-dpi-below-recommended'
  | 'unknown-image-resolution'
  | 'missing-font'
  | 'text-too-small'
  | 'detail-too-small'
  | 'object-opacity-too-low'
  | 'transparency-not-allowed'
  | 'unsupported-colour-mode'
  | 'max-print-size-exceeded'
  | 'a4-sheet-overflow';

export interface ValidationResult {
  id: string;
  code: ValidationCode;
  severity: ValidationSeverity;
  message: string;
  viewId?: ProductViewId;
  objectId?: string;
  acknowledged: boolean;
  details?: Record<string, string | number | boolean | null>;
}

export interface ValidationOptions {
  /** Restrict checks to selected surfaces, for example a single-view export. */
  viewIds?: ProductViewId[];
  /** Additional font IDs made available by the host application. */
  availableFontIds?: Iterable<string>;
  /** Known alpha information supplied by the upload/asset inspection pipeline. */
  assetTransparency?: Readonly<Record<string, boolean | 'unknown'>>;
  includeHiddenObjects?: boolean;
}

export interface ValidationReport {
  results: ValidationResult[];
  errors: ValidationResult[];
  warnings: ValidationResult[];
  unacknowledgedWarnings: ValidationResult[];
  /** True only when errors are absent and every warning was acknowledged. */
  canExport: boolean;
}

const MINIMUM_CUTTABLE_OBJECT_OPACITY = 8 / 255;

function issueId(code: ValidationCode, viewId?: ProductViewId, objectId?: string): string {
  return [code, viewId ?? 'design', objectId ?? 'surface'].join(':');
}

function createIssue(
  design: DesignDocument,
  code: ValidationCode,
  severity: ValidationSeverity,
  message: string,
  viewId?: ProductViewId,
  objectId?: string,
  details?: ValidationResult['details'],
): ValidationResult {
  const id = issueId(code, viewId, objectId);
  return {
    id,
    code,
    severity,
    message,
    ...(viewId ? { viewId } : {}),
    ...(objectId ? { objectId } : {}),
    acknowledged: severity === 'warning' && design.acknowledgedWarnings.includes(id),
    ...(details ? { details } : {}),
  };
}

function safeRect(
  width: number,
  height: number,
  area: ProductConfig['printableAreas'][number],
): MmRect {
  return {
    x: area.safeZoneMm.left,
    y: area.safeZoneMm.top,
    width: width - area.safeZoneMm.left - area.safeZoneMm.right,
    height: height - area.safeZoneMm.top - area.safeZoneMm.bottom,
  };
}

function hasAlphaColour(colour: string): boolean {
  const normalized = colour.trim().toLowerCase();
  const shortHex = /^#[0-9a-f]{4}$/.exec(normalized);
  if (shortHex) return normalized[4] !== 'f';
  const longHex = /^#[0-9a-f]{8}$/.exec(normalized);
  if (longHex) return normalized.slice(7, 9) !== 'ff';
  const functional = /^(rgba|hsla|rgb|hsl)\((.*)\)$/.exec(normalized);
  if (!functional) return false;
  const functionName = functional[1]!;
  const body = functional[2]!;
  if ((functionName === 'rgb' || functionName === 'hsl') && !body.includes('/')) return false;
  const alpha = body.split(/[,/]/).at(-1)?.trim();
  if (!alpha) return false;
  return alpha.endsWith('%') ? Number.parseFloat(alpha) < 100 : Number.parseFloat(alpha) < 1;
}

function objectUsesTransparency(
  object: DesignObject,
  options: ValidationOptions,
  asset?: AssetRecord,
): boolean {
  if (object.opacity < 1) return true;
  if (
    object.type === 'shape' &&
    (hasAlphaColour(object.fill) || (object.stroke && hasAlphaColour(object.stroke.colour)))
  )
    return true;
  if (object.type === 'drawing' && hasAlphaColour(object.stroke.colour)) return true;
  if (
    object.type === 'text' &&
    (hasAlphaColour(object.colour) ||
      (object.stroke && hasAlphaColour(object.stroke.colour)) ||
      (object.shadow && hasAlphaColour(object.shadow.colour)))
  )
    return true;
  if (object.type !== 'image') return false;
  return (
    Boolean(object.transparentColour) ||
    Boolean(object.mask) ||
    object.borders?.some(border => hasAlphaColour(border.colour)) === true ||
    asset?.transparency === 'present' ||
    options.assetTransparency?.[object.assetId] === true
  );
}

function checkImage(
  issues: ValidationResult[],
  design: DesignDocument,
  product: ProductConfig,
  viewId: ProductViewId,
  object: DesignImageObject,
): void {
  const asset = design.assets[object.assetId];
  if (!asset || asset.kind !== 'image') {
    issues.push(
      createIssue(
        design,
        'missing-asset',
        'error',
        'Obrázkový podklad chýba alebo má nesprávny typ.',
        viewId,
        object.id,
      ),
    );
    return;
  }

  if (asset.pixelSize && activeImageBorders(object.borders).length === 0) {
    const expectedAspectRatio =
      (asset.pixelSize.width * object.crop.width) / (asset.pixelSize.height * object.crop.height);
    const actualAspectRatio = object.transform.sizeMm.width / object.transform.sizeMm.height;
    const distortion = Math.abs(actualAspectRatio / expectedAspectRatio - 1);
    if (
      Number.isFinite(expectedAspectRatio) &&
      Number.isFinite(actualAspectRatio) &&
      expectedAspectRatio > 0 &&
      actualAspectRatio > 0 &&
      distortion > 0.01
    ) {
      issues.push(
        createIssue(
          design,
          'distorted-image-aspect',
          'error',
          'Obrázok je natiahnutý a nemá pôvodný pomer strán. V editore upravte jeho šírku alebo výšku.',
          viewId,
          object.id,
          {
            expectedAspectRatio: Math.round(expectedAspectRatio * 1000) / 1000,
            actualAspectRatio: Math.round(actualAspectRatio * 1000) / 1000,
            distortionPercent: Math.round(distortion * 1000) / 10,
          },
        ),
      );
    }
  }

  if (asset.mimeType === 'image/svg+xml') {
    // SVG viewport dimensions define geometry, not a finite raster resolution.
    // The production loader keeps SVG on the HTMLImageElement vector path and
    // rasterizes it directly at the requested export DPI.
  } else if (!asset.pixelSize) {
    issues.push(
      createIssue(
        design,
        'unknown-image-resolution',
        'warning',
        'Rozlíšenie obrázka nie je známe; výsledné DPI sa nedá overiť.',
        viewId,
        object.id,
      ),
    );
  } else {
    const usedPixelsX = asset.pixelSize.width * object.crop.width;
    const usedPixelsY = asset.pixelSize.height * object.crop.height;
    const dpiX = usedPixelsX / (object.transform.sizeMm.width / 25.4);
    const dpiY = usedPixelsY / (object.transform.sizeMm.height / 25.4);
    const effectiveDpi = Math.min(dpiX, dpiY);
    const details = {
      effectiveDpi: Math.round(effectiveDpi * 10) / 10,
      dpiX: Math.round(dpiX * 10) / 10,
      dpiY: Math.round(dpiY * 10) / 10,
      requiredDpi: product.productionRules.minimumDpi,
      recommendedDpi: product.productionRules.recommendedDpi,
    };
    if (effectiveDpi < product.productionRules.minimumDpi) {
      issues.push(
        createIssue(
          design,
          'low-image-dpi',
          'error',
          `Obrázok má iba ${Math.round(effectiveDpi)} DPI; minimum je ${product.productionRules.minimumDpi} DPI. Zmenšite obrázok alebo nahrajte kvalitnejší.`,
          viewId,
          object.id,
          details,
        ),
      );
    } else if (effectiveDpi < product.productionRules.recommendedDpi) {
      issues.push(
        createIssue(
          design,
          'image-dpi-below-recommended',
          'warning',
          `Obrázok má ${Math.round(effectiveDpi)} DPI; odporúčame ${product.productionRules.recommendedDpi} DPI. Pre ostrejšiu potlač ho zmenšite alebo nahrajte kvalitnejší obrázok.`,
          viewId,
          object.id,
          details,
        ),
      );
    }
  }

  checkColourMode(issues, design, product, viewId, object, asset);
}

function checkColourMode(
  issues: ValidationResult[],
  design: DesignDocument,
  product: ProductConfig,
  viewId: ProductViewId,
  object: DesignImageObject,
  asset: AssetRecord,
): void {
  const mode = asset.colourMode ?? 'unknown';
  // Browsers do not expose reliable RGB/CMYK metadata for many otherwise valid
  // uploads. Unknown metadata is therefore not actionable for the customer;
  // only an explicitly detected unsupported mode should block production.
  if (mode === 'unknown') return;
  if (product.productionRules.acceptedColourModes.includes(mode)) return;
  issues.push(
    createIssue(
      design,
      'unsupported-colour-mode',
      'error',
      `Farebný režim ${mode.toUpperCase()} nie je pre tento produkt podporovaný.`,
      viewId,
      object.id,
      { colourMode: mode },
    ),
  );
}

function checkText(
  issues: ValidationResult[],
  design: DesignDocument,
  product: ProductConfig,
  viewId: ProductViewId,
  object: DesignTextObject,
  availableFontIds: Set<string>,
): void {
  const fontAsset = design.assets[object.fontAssetId];
  const embeddedFontAvailable = fontAsset?.kind === 'font' && fontAsset.embedded;
  if (!availableFontIds.has(object.fontAssetId) && !embeddedFontAvailable) {
    issues.push(
      createIssue(
        design,
        'missing-font',
        'error',
        `Písmo „${object.fontFamily}“ sa nepodarilo načítať. Vyberte iné písmo.`,
        viewId,
        object.id,
        { fontAssetId: object.fontAssetId, fontFamily: object.fontFamily },
      ),
    );
  }
  const physicalTextHeightMm = effectiveTextHeightMm(object);
  if (physicalTextHeightMm < product.productionRules.minimumTextHeightMm) {
    issues.push(
      createIssue(
        design,
        'text-too-small',
        'warning',
        `Text je menší než výrobné minimum ${product.productionRules.minimumTextHeightMm} mm.`,
        viewId,
        object.id,
        {
          fontSizeMm: object.fontSizeMm,
          physicalTextHeightMm: Math.round(physicalTextHeightMm * 100) / 100,
          transformHeightMm: object.transform.sizeMm.height,
          minimumTextHeightMm: product.productionRules.minimumTextHeightMm,
        },
      ),
    );
  }
  if (
    object.stroke &&
    object.stroke.widthMm > 0 &&
    object.stroke.widthMm < product.productionRules.minimumDetailMm
  ) {
    issues.push(
      createIssue(
        design,
        'detail-too-small',
        'warning',
        `Obrys textu je tenší než ${product.productionRules.minimumDetailMm} mm.`,
        viewId,
        object.id,
        {
          detailMm: object.stroke.widthMm,
          minimumDetailMm: product.productionRules.minimumDetailMm,
        },
      ),
    );
  }
}

function checkDetail(
  issues: ValidationResult[],
  design: DesignDocument,
  product: ProductConfig,
  viewId: ProductViewId,
  object: DesignObject,
): void {
  if (object.type !== 'shape' && object.type !== 'drawing') return;
  const candidates =
    object.type === 'drawing'
      ? [object.stroke.widthMm]
      : [
          object.transform.sizeMm.width,
          object.transform.sizeMm.height,
          ...(object.stroke ? [object.stroke.widthMm] : []),
        ];
  const minimum = Math.min(...candidates.filter(value => value > 0));
  if (minimum < product.productionRules.minimumDetailMm) {
    issues.push(
      createIssue(
        design,
        'detail-too-small',
        'warning',
        `Detail je menší než výrobné minimum ${product.productionRules.minimumDetailMm} mm.`,
        viewId,
        object.id,
        { detailMm: minimum, minimumDetailMm: product.productionRules.minimumDetailMm },
      ),
    );
  }
}

function sameSize(
  a: { width: number; height: number },
  b: { width: number; height: number },
): boolean {
  return Math.abs(a.width - b.width) < 1e-7 && Math.abs(a.height - b.height) < 1e-7;
}

export function validateProductionDesign(
  design: DesignDocument,
  product: ProductConfig,
  options: ValidationOptions = {},
): ValidationResult[] {
  const issues: ValidationResult[] = [];
  const restrictedViews = options.viewIds ? new Set(options.viewIds) : undefined;
  const availableFontIds = new Set(fonts.map(font => font.id));
  if (options.availableFontIds) {
    for (const fontId of options.availableFontIds) availableFontIds.add(fontId);
  }

  if (!product.supportedTechnologies.includes(design.technology)) {
    issues.push(
      createIssue(
        design,
        'unsupported-technology',
        'error',
        'Zvolená technológia potlače nie je pre produkt podporovaná.',
        undefined,
        undefined,
        { technology: design.technology },
      ),
    );
  }

  const hasVisibleArtwork = Object.entries(design.surfaces).some(([viewId, surface]) => {
    if (restrictedViews && !restrictedViews.has(viewId as ProductViewId)) return false;
    return surface?.objects.some(
      object => options.includeHiddenObjects || isVisibleArtworkObject(object),
    );
  });
  if (!hasVisibleArtwork) {
    issues.push(
      createIssue(
        design,
        'empty-design',
        'error',
        'Pridajte aspoň jeden viditeľný prvok potlače na prednú alebo zadnú stranu.',
      ),
    );
  }

  const selectedVariant = resolveProductVariant(product, design.variantId);
  if (!selectedVariant) {
    issues.push(
      createIssue(
        design,
        'invalid-product-variant',
        'error',
        'Zvolený strih trička už v katalógu neexistuje. Vyberte strih a veľkosť znova.',
        undefined,
        undefined,
        { variantId: design.variantId },
      ),
    );
  } else if (!isSizeSelectable(selectedVariant, design.size)) {
    issues.push(
      createIssue(
        design,
        'invalid-product-size',
        'error',
        `Veľkosť ${design.size || '–'} nie je dostupná pre zvolený strih ${selectedVariant.label}.`,
        undefined,
        undefined,
        { variantId: design.variantId, size: design.size },
      ),
    );
  } else if (selectedVariant.sizeProfiles?.length) {
    try {
      resolveSizeProfile(product, design.variantId, design.size);
    } catch {
      issues.push(
        createIssue(
          design,
          'missing-size-profile',
          'error',
          `Pre veľkosť ${design.size} chýbajú reálne rozmery odevu. Export je zablokovaný, aby sa potlač nevyrobila v nesprávnom pomere.`,
          undefined,
          undefined,
          { variantId: design.variantId, size: design.size },
        ),
      );
    }
  }
  if (
    selectedVariant &&
    !isColourAvailableForVariant(product, selectedVariant.id, design.colourId)
  ) {
    issues.push(
      createIssue(
        design,
        'invalid-product-colour',
        'error',
        `Farba ${design.colourId || '–'} nie je dostupná pre variant ${selectedVariant.label}.`,
        undefined,
        undefined,
        { variantId: selectedVariant.id, colourId: design.colourId },
      ),
    );
  }

  const allViewIds = Object.keys(design.surfaces) as ProductViewId[];
  for (const viewId of allViewIds) {
    if (restrictedViews && !restrictedViews.has(viewId)) continue;
    const surface = design.surfaces[viewId];
    if (!surface) continue;
    const objects = surface.objects.filter(
      object => options.includeHiddenObjects || isVisibleArtworkObject(object),
    );
    const baseArea = product.printableAreas.find(candidate => candidate.viewId === viewId);
    let area = baseArea;
    if (baseArea && selectedVariant && isSizeSelectable(selectedVariant, design.size)) {
      try {
        area = resolvePrintableArea(product, design.variantId, design.size, viewId);
      } catch {
        // The selection/profile issue above is more useful than a second resolver error.
      }
    }
    if (!area) {
      if (objects.length > 0) {
        issues.push(
          createIssue(
            design,
            'unsupported-view',
            'error',
            'Dizajn obsahuje tlačové objekty na strane, ktorú produkt nepodporuje.',
            viewId,
          ),
        );
      }
      continue;
    }

    if (!area.supportedTechnologies.includes(design.technology)) {
      issues.push(
        createIssue(
          design,
          'unsupported-technology',
          'error',
          'Zvolená technológia potlače nie je na tejto strane podporovaná.',
          viewId,
          undefined,
          { technology: design.technology },
        ),
      );
    }
    if (!sameSize(surface.printAreaMm, area.sizeMm)) {
      issues.push(
        createIssue(
          design,
          'surface-size-mismatch',
          'error',
          'Fyzický rozmer návrhu sa nezhoduje s tlačovou oblasťou produktu.',
          viewId,
          undefined,
          {
            designWidthMm: surface.printAreaMm.width,
            designHeightMm: surface.printAreaMm.height,
            productWidthMm: area.sizeMm.width,
            productHeightMm: area.sizeMm.height,
          },
        ),
      );
    }

    if (hasActivePrintEffects(surface.printEffects) && !product.productionRules.allowTransparency) {
      issues.push(
        createIssue(
          design,
          'transparency-not-allowed',
          'error',
          'Efekt opotrebovania vytvára priehľadné miesta, ktoré zvolená výrobná technológia nepovoľuje.',
          viewId,
        ),
      );
    }

    const printableRect: MmRect = {
      x: 0,
      y: 0,
      width: area.sizeMm.width,
      height: area.sizeMm.height,
    };
    const innerSafeRect = safeRect(area.sizeMm.width, area.sizeMm.height, area);
    for (const object of objects) {
      if (object.opacity > 0 && object.opacity < MINIMUM_CUTTABLE_OBJECT_OPACITY) {
        issues.push(
          createIssue(
            design,
            'object-opacity-too-low',
            'error',
            'Priehľadnosť objektu je príliš nízka na spoľahlivé vytvorenie rezacej kontúry.',
            viewId,
            object.id,
            {
              opacityPercent: Math.round(object.opacity * 1000) / 10,
              minimumOpacityPercent: Math.round(MINIMUM_CUTTABLE_OBJECT_OPACITY * 1000) / 10,
            },
          ),
        );
      }

      if (!rectContainsObject(printableRect, object)) {
        issues.push(
          createIssue(
            design,
            'object-out-of-bounds',
            'error',
            'Objekt presahuje mimo tlačovej oblasti.',
            viewId,
            object.id,
          ),
        );
      } else if (!rectContainsObject(innerSafeRect, object)) {
        issues.push(
          createIssue(
            design,
            'object-outside-safe-zone',
            'warning',
            'Objekt zasahuje do okrajovej bezpečnostnej zóny.',
            viewId,
            object.id,
          ),
        );
      }

      const objectAsset = object.type === 'image' ? design.assets[object.assetId] : undefined;
      if (
        objectUsesTransparency(object, options, objectAsset) &&
        !product.productionRules.allowTransparency
      ) {
        issues.push(
          createIssue(
            design,
            'transparency-not-allowed',
            'error',
            'Zvolená výrobná technológia nepovoľuje priehľadnosť.',
            viewId,
            object.id,
          ),
        );
      }
      if (object.type === 'image') checkImage(issues, design, product, viewId, object);
      if (object.type === 'text')
        checkText(issues, design, product, viewId, object, availableFontIds);
      checkDetail(issues, design, product, viewId, object);
    }

    const contentBounds = unionRects(objects.map(objectBounds));
    const workflow = product.productionRules.workflow;
    const transferGroups = !contentBounds
      ? []
      : workflow?.kind === 'print-cut-transfer'
        ? partitionObjectsIntoA4Transfers(objects, workflow.pagePaddingMm)
        : [{ objectIds: objects.map(object => object.id), artworkBoundsMm: contentBounds }];
    if (workflow?.kind === 'print-cut-transfer') {
      for (const transferGroup of transferGroups) {
        const fit = inspectA4SheetFit(transferGroup.artworkBoundsMm, workflow.pagePaddingMm);
        if (!fit.fits) {
          const objectId =
            transferGroup.objectIds.length === 1 ? transferGroup.objectIds[0] : undefined;
          issues.push(
            createIssue(
              design,
              'a4-sheet-overflow',
              'error',
              formatA4SheetBlockingMessage(transferGroup.artworkBoundsMm, workflow.pagePaddingMm) ??
                'Motív sa nezmestí v mierke 1 : 1 na jeden hárok A4.',
              viewId,
              objectId,
              {
                contentWidthMm: Math.round(transferGroup.artworkBoundsMm.width * 100) / 100,
                contentHeightMm: Math.round(transferGroup.artworkBoundsMm.height * 100) / 100,
                sheetWidthMm: 210,
                sheetHeightMm: 297,
                marginMm: fit.error.marginMm,
                portraitMaximumWidthMm: fit.error.portraitPrintableSizeMm.width,
                portraitMaximumHeightMm: fit.error.portraitPrintableSizeMm.height,
                landscapeMaximumWidthMm: fit.error.landscapePrintableSizeMm.width,
                landscapeMaximumHeightMm: fit.error.landscapePrintableSizeMm.height,
              },
            ),
          );
        }
      }
    }
    for (const transferGroup of transferGroups) {
      if (
        transferGroup.artworkBoundsMm.width <= area.maxPrintSizeMm.width + 1e-7 &&
        transferGroup.artworkBoundsMm.height <= area.maxPrintSizeMm.height + 1e-7
      ) {
        continue;
      }
      const objectId =
        transferGroup.objectIds.length === 1 ? transferGroup.objectIds[0] : undefined;
      issues.push(
        createIssue(
          design,
          'max-print-size-exceeded',
          'error',
          `Motív má ${Math.round(transferGroup.artworkBoundsMm.width * 10) / 10} × ${Math.round(transferGroup.artworkBoundsMm.height * 10) / 10} mm a presahuje maximálnu povolenú plochu potlače ${area.maxPrintSizeMm.width} × ${area.maxPrintSizeMm.height} mm.`,
          viewId,
          objectId,
          {
            contentWidthMm: Math.round(transferGroup.artworkBoundsMm.width * 100) / 100,
            contentHeightMm: Math.round(transferGroup.artworkBoundsMm.height * 100) / 100,
            maximumWidthMm: area.maxPrintSizeMm.width,
            maximumHeightMm: area.maxPrintSizeMm.height,
          },
        ),
      );
    }
  }

  return issues;
}

export function createValidationReport(results: ValidationResult[]): ValidationReport {
  const errors = results.filter(result => result.severity === 'error');
  const warnings = results.filter(result => result.severity === 'warning');
  const unacknowledgedWarnings = warnings.filter(result => !result.acknowledged);
  return {
    results,
    errors,
    warnings,
    unacknowledgedWarnings,
    canExport: errors.length === 0 && unacknowledgedWarnings.length === 0,
  };
}

export class ProductionValidationError extends Error {
  readonly report: ValidationReport;

  constructor(report: ValidationReport) {
    super(
      `Production export blocked by ${report.errors.length} error(s) and ${report.unacknowledgedWarnings.length} unacknowledged warning(s)`,
    );
    this.name = 'ProductionValidationError';
    this.report = report;
  }
}

export function assertProductionReady(
  results: ValidationResult[],
  allowUnacknowledgedWarnings = false,
): ValidationReport {
  const report = createValidationReport(results);
  if (
    report.errors.length > 0 ||
    (!allowUnacknowledgedWarnings && report.unacknowledgedWarnings.length > 0)
  ) {
    throw new ProductionValidationError(report);
  }
  return report;
}
